/**
 * Wizard de bienvenida "entrá al mundo Golden Parce".
 *
 * Es el primer contacto del promotor con la app. Pantalla épica (imagen GP dorada
 * con fade-in + texto que se escribe solo) y, debajo, la elección de cómo usarla:
 *   - INSTALAR (botón estrella, recomendado): según el entorno dispara el prompt
 *     nativo (Android), la guía "Compartir → Agregar a inicio" (iOS Safari), o el
 *     aviso "abrí en el navegador" (dentro de WhatsApp/Instagram).
 *   - Seguir en el navegador (link secundario): cae al flujo actual sin tocar nada.
 *
 * La detección de entorno y el prompt viven en módulos SSOT (deteccion-entorno,
 * pwa-install). Acá solo está la coreografía visual y el ruteo de la elección.
 */

import { t } from '../languages.js';
import { escribirTexto } from '../systems/motion.js';
import { caminoDeInstalacion } from '../systems/deteccion-entorno.js';
import { manejarClickInstalar, mostrarGuiaInstalacion } from '../pwa-install.js';

// Imagen de bienvenida optimizada (WebP ~133KB). Si el WebView no soporta WebP
// (rarísimo en 2026), el navegador muestra el alt; no rompe el flujo.
const IMAGEN_BIENVENIDA = '/assets/gp-background.webp';

/**
 * Renderiza el wizard de bienvenida.
 * @param {{ onSeguirWeb: () => void, codigo?: string }} cb - onSeguirWeb continúa el
 *        flujo normal (canje/login/activación). codigo: el código de acceso de la URL,
 *        para mostrarlo COPIABLE en la guía iOS (la app instalada no lo recibe solo).
 * @returns {HTMLElement}
 */
export function renderOnboarding({ onSeguirWeb, codigo }) {
    const el = document.createElement('div');
    el.className = 'pm-bienvenida';
    el.innerHTML = `
        <div class="pm-bienvenida-hero">
            <img class="pm-bienvenida-img pm-fade-in" src="${IMAGEN_BIENVENIDA}" alt="Golden Parce" />
            <div class="pm-bienvenida-saludo pm-typewriter" id="pm-bv-saludo"></div>
            <div class="pm-bienvenida-frase pm-typewriter" id="pm-bv-frase"></div>
        </div>
        <div class="pm-bienvenida-cta" id="pm-bv-cta" style="opacity:0; pointer-events:none;">
            <button class="btn btn-install pm-bienvenida-instalar" id="pm-bv-instalar">
                <span class="pm-bienvenida-estrella">⭐</span>
                <span class="pm-bienvenida-instalar-txt">
                    <span class="pm-bienvenida-instalar-tit">${t('bienvenida.instalarBoton')}</span>
                    <span class="pm-bienvenida-instalar-sub">${t('bienvenida.instalarSub')}</span>
                </span>
                <span class="pm-bienvenida-badge">${t('bienvenida.instalarRecomendado')}</span>
            </button>
            <button class="pm-bienvenida-web" id="pm-bv-web">${t('bienvenida.seguirWeb')}</button>
        </div>`;

    const saludoEl = el.querySelector('#pm-bv-saludo');
    const fraseEl = el.querySelector('#pm-bv-frase');
    const ctaEl = el.querySelector('#pm-bv-cta');

    // Coreografía: imagen fade-in (CSS) → se escribe el saludo → la frase → aparece
    // el CTA. Cada escritura espera a la anterior (Promise). Si el navegador respeta
    // prefers-reduced-motion, escribirTexto pinta todo de una y la secuencia es casi
    // instantánea (sin marear). El AbortController corta la animación si el promotor
    // elige (web/instalar) antes de que termine — la vista se reemplaza y no queremos
    // que la Promise siga escribiendo en nodos huérfanos.
    const ctrlCoreografia = new AbortController();
    const signal = ctrlCoreografia.signal;
    (async () => {
        await escribirTexto(saludoEl, t('bienvenida.saludo'), { msPorCaracter: 55, signal });
        await escribirTexto(fraseEl, t('bienvenida.frase'), { delay: 150, msPorCaracter: 28, signal });
        ctaEl.style.transition = 'opacity 0.5s ease';
        ctaEl.style.opacity = '1';
        ctaEl.style.pointerEvents = 'auto'; // recién acá los botones son tocables
    })();

    // Si está dentro de la app instalada o no se puede instalar, igual mostramos el
    // botón pero el handler decide qué hacer; el botón web siempre está.
    const camino = caminoDeInstalacion();
    if (camino === 'instalada') {
        // Ya instalada: instalar no tiene sentido → ocultar el botón estrella.
        const btnInstalar = el.querySelector('#pm-bv-instalar');
        if (btnInstalar) btnInstalar.style.display = 'none';
    }

    el.querySelector('#pm-bv-instalar').addEventListener('click', async () => {
        ctrlCoreografia.abort(); // cortar el typewriter si seguía corriendo
        // Android con prompt diferido → prompt nativo. iOS / in-app / otros → guía
        // contextual. Todo enruteado por pwa-install según el camino detectado.
        const lanzoPromptNativo = await manejarClickInstalar();
        if (!lanzoPromptNativo) {
            // No había prompt nativo (iOS, in-app, etc.) → guía contextual, con el
            // código copiable arriba (en iOS la app instalada no lo recibe solo).
            mostrarGuiaInstalacion(camino, codigo);
        }
    });

    el.querySelector('#pm-bv-web').addEventListener('click', () => {
        ctrlCoreografia.abort(); // la vista se reemplaza: cortar la animación en curso
        if (onSeguirWeb) onSeguirWeb();
    });

    return el;
}
